import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, useCallback, useContext, useLayoutEffect, useRef, useState } from "react";
import { useStringMap } from "../../utilities/string-map/useStringMap";
import { CancelXIcon } from "../../icons/CancelXIcon";
import { ConfirmCheckmarkIcon } from "../../icons/ConfirmCheckmarkIcon";
import { EditIcon } from "../../icons/EditIcon";
import { useCombineRefs } from "../../hooks/useCombineRefs";
import { useFocusOnMount } from "../../hooks/useFocusOnMount";
import { usePrevious } from "../../hooks/usePrevious";
import ClickAwayListener from "../../vendor/mui-base/ClickAwayListener";
import { Input } from "../input/Input";
import { FieldsetContext } from "../fieldset/Fieldset";
import { ScreenReaderContent } from "../../screen-reader-content/ScreenReaderContent";
import * as Styled from "./EditableText.styled";
import { stringMapDefault } from "./EditableText.stringMap";
const componentName = "EditableText";
export const EditableText = forwardRef((props, ref) => {
    const { stringMap: stringMapProp, isOpen: isOpenProp, defaultIsOpen = false, isReadOnly: isReadOnlyProp = false, inputProps, onClose, onOpen, onUpdate, placeholder, defaultValue = "", value, shouldHideActions = false, typeStyle = "bodyS", ...otherProps } = props;
    const stringMap = useStringMap(componentName, { stringMapDefault, stringMapProp });
    const fieldsetOptions = useContext(FieldsetContext);
    const isReadOnly = isReadOnlyProp ? isReadOnlyProp : fieldsetOptions.disabled;
    const [isOpenState, setIsOpenState] = useState(isOpenProp === undefined ? defaultIsOpen : undefined);
    const [internalValue, setInternalValue] = useState(value ?? defaultValue);
    const [restoreValue, setRestoreValue] = useState(value ?? defaultValue);
    const previewRef = useRef(null);
    const inputRef = useRef(null);
    const actionsRef = useRef(null);
    const containerRef = useRef(null);
    const mergedRef = useCombineRefs(containerRef, ref);
    const { shouldFocusOnMount, handleFocus, handleBlur, handleMaybeNavigate } = useFocusOnMount({
        containerRef,
        initialValue: isOpenProp === true
    });
    const isControlledOpen = isOpenProp !== undefined;
    const isOpen = isControlledOpen ? isOpenProp : isOpenState;
    useLayoutEffect(() => {
        if (isControlledOpen && !(onClose && onOpen)) {
            // eslint-disable-next-line no-console -- warning about incorrect API usage
            console.warn(`[ALCHEMY] <EditableText> is used in controlled mode (isOpen) but is missing an \`onOpen\` or \`onClose\` callback. This is not supported.`, containerRef.current);
        }
    }, [isControlledOpen, onClose, onOpen]);
    const prevVal = useRef();
    const previousValue = prevVal.current;
    // Fix to keep external props and internal state synchronized DES-2399
    if (value !== undefined && value !== previousValue) {
        prevVal.current = value;
        setInternalValue(value);
        setRestoreValue(value);
    }
    const wasOpen = !usePrevious(isOpen);
    useLayoutEffect(() => {
        if (!shouldFocusOnMount && !isOpen) {
            // Consumers expect this to always grab focus when switching from preview to edit mode
            // TODO: provide an explicit autofocus option or a handler to manually focus if using controlled isOpen mode
            return;
        }
        if (wasOpen && isOpen && inputRef.current) {
            inputRef.current.focus();
        }
        else if (!wasOpen && !isOpen && previewRef.current) {
            previewRef.current.focus();
        }
    }, [isOpen, shouldFocusOnMount, wasOpen]);
    const startEditing = () => {
        onOpen?.();
        if (isOpenProp === undefined) {
            setIsOpenState(true);
        }
    };
    const stopEditing = useCallback(() => {
        onClose?.();
        if (isOpenProp === undefined) {
            setIsOpenState(false);
        }
    }, [isOpenProp, onClose]);
    const handleConfirm = useCallback(() => {
        onUpdate?.(internalValue);
        if (value === undefined) {
            // uncontrolled
            setRestoreValue(internalValue);
        }
        stopEditing();
    }, [onUpdate, internalValue, value, stopEditing]);
    const handleCancel = useCallback(() => {
        handleMaybeNavigate();
        setInternalValue(restoreValue);
        stopEditing();
    }, [handleMaybeNavigate, restoreValue, stopEditing]);
    const handleClickAway = isOpen
        ? () => {
            handleConfirm();
        }
        : () => { };
    const previewValue = value ?? internalValue;
    const hasValue = previewValue !== undefined && previewValue !== "";
    const hasPlaceholder = placeholder !== undefined;
    const renderEditing = () => (_jsxs(_Fragment, { children: [_jsx(Input, { ref: inputRef, isFullWidth: true, value: internalValue, placeholder: placeholder, ...inputProps, onChange: (e) => {
                    setInternalValue(e.target.value);
                    inputProps?.onChange?.(e);
                }, onKeyDown: (event) => {
                    // Call use-provided input callback
                    inputProps?.onKeyDown?.(event);
                    if (event.isDefaultPrevented()) {
                        return;
                    }
                    // Confirm or cancel editing on "enter"
                    if (event.key === "Enter") {
                        event.preventDefault();
                        event.stopPropagation();
                        handleMaybeNavigate();
                        handleConfirm();
                    }
                    // Exit edit mode on escape
                    if (event.key === "Escape") {
                        event.preventDefault();
                        event.stopPropagation();
                        handleCancel();
                    }
                } }), _jsxs(Styled.InputActionWrapper, { isVisuallyHidden: shouldHideActions, ref: actionsRef, children: [_jsx(Styled.InputActionButton, { type: "button", tabIndex: shouldHideActions ? -1 : undefined, "data-testid": "EditableTextConfirmButton", onClick: () => {
                            handleMaybeNavigate();
                            handleConfirm();
                        }, children: _jsx(ConfirmCheckmarkIcon, { "aria-label": stringMap.confirmIconTitle }) }), _jsx(Styled.InputActionButton, { type: "button", tabIndex: shouldHideActions ? -1 : undefined, "data-testid": "EditableTextCancelButton", onClick: () => handleCancel(), children: _jsx(CancelXIcon, { "aria-label": stringMap.cancelIconTitle }) })] })] }));
    const handleEdit = () => {
        handleMaybeNavigate();
        startEditing();
    };
    const renderPreview = () => (_jsxs(Styled.Preview, { typeStyle: typeStyle, children: [_jsx(Styled.PreviewText, { hasValue: hasValue, hasPlaceholder: hasPlaceholder, children: hasValue ? previewValue : placeholder }), _jsxs(Styled.EditButton, { "data-testid": "EditableTextEditButton", ref: previewRef, onClick: handleEdit, children: [_jsx(EditIcon, { "aria-hidden": "true" }), _jsx(ScreenReaderContent, { children: stringMap.editIconTitle })] })] }));
    const renderContent = () => {
        if (isReadOnly) {
            return _jsx(Styled.ReadOnlyPreview, { typeStyle: typeStyle, children: previewValue });
        }
        return isOpen ? renderEditing() : renderPreview();
    };
    return (_jsx(ClickAwayListener, { mouseEvent: "onMouseDown" // prevent triggering clickaway on a button that opens an editable text
        , onClickAway: handleClickAway, children: _jsx(Styled.EditableText, { ref: mergedRef, "data-testid": "EditableText", typeStyle: typeStyle, onFocus: handleFocus, onBlur: handleBlur, ...otherProps, children: renderContent() }) }));
});
EditableText.displayName = componentName;
EditableText.defaultProps = {
    defaultIsOpen: false,
    shouldHideActions: false,
    isReadOnly: false,
    typeStyle: "bodyS"
};
